/**
 * Server-side pose over HTTP — the whole network surface, in one class.
 *
 * ── The design target, and why it is not "go as fast as possible" ────────
 *
 * Measured 2026-09-09 on real handsets (plan §2.4a): a steady **2.3 Hz beat a
 * bursty 4.2 Hz**. The alignment gate is a *coincidence* test — all four joints
 * inside their boxes at once — and the rotation is a *deadline* test, a fixed
 * 250 ms tick. Both are defeated by jitter rather than by mean rate. The
 * Android that had never completed a scan completed one at 2.3 Hz with
 * `correctPostureLostCount` of **0**, against 4.2 Hz and **11** drops
 * on-device.
 *
 * So this client optimises for evenness:
 *
 *  - **A bounded in-flight window — which overlaps the ROTATION, not the
 *    alignment stream.** Rotation ticks are fire-and-forget on a fixed 250 ms
 *    interval, so on a slow round trip two are in flight at once and the window
 *    stops a tick waiting on the one before it. Alignment never uses it:
 *    `useDetectionEffects` awaits each `processFrame` before it takes the next
 *    frame, so the alignment stream is strictly one frame per round trip —
 *    `1 / (RTT + inference)`, the spike's 2.3 Hz at 322 ms and 0.6 Hz at
 *    764 ms. Steady beats fast (above), so that is the design, not a defect;
 *    but do not reason about alignment as if this window pipelined it.
 *  - **A late frame is DROPPED, never queued.** A queue converts latency into
 *    unbounded staleness: the gate would be judging a body position seconds
 *    old, and the rotation FSM would pair a pose with the wrong tick. Dropping
 *    is also why the window is small — a big window is a queue with extra steps.
 *  - **Every failure resolves `[]`.** Never rejects. A dropped frame then looks
 *    exactly like a frame the detector could not resolve, which every consumer
 *    (`processFrame`'s no-pose branch, `RotationManager`'s `Unknown` state)
 *    already handles. A rejection would surface as an `error` event and trip
 *    machinery built for a different fault.
 *
 * ── Everything external is injected ─────────────────────────────────────
 *
 * `fetch`, the frame encoder, the token source and the clock. That is what lets
 * the behaviour above be tested for real instead of against a mocked network.
 */

import type { Pose } from "@tensorflow-models/pose-detection";

/**
 * 3:4, matching `useProcessVideoFrame`'s crop. Long edge 384 clears
 * EfficientDet-Lite0's 320 input; short edge 288 clears MoveNet's 192 with room
 * for its letterbox. ~12 KB at q0.6 → ~0.77 Mbps at 8 Hz (plan §3.3).
 */
export const SEND_WIDTH = 288;
export const SEND_HEIGHT = 384;

/**
 * A frame later than this is useless to the gate, and on the rotation path it is
 * later than the next tick. Fail it and move on rather than holding a slot.
 */
const DEFAULT_TIMEOUT_MS = 1500;

/**
 * Two concurrent requests, not more.
 *
 * Only the rotation's fire-and-forget ticks ever overlap — alignment is serial
 * by its caller (see the header). For them, one would make every tick wait out
 * the previous round trip; many would make a queue, and a queue is staleness.
 * Two covers one round trip of overlap while keeping the worst-case staleness
 * of a delivered pose at roughly one round trip.
 */
const DEFAULT_MAX_IN_FLIGHT = 2;

/** Below this, a p50 is not a measurement. */
const MIN_SAMPLES_TO_JUDGE = 3;

/**
 * Unbroken server-side failures after which the connection counts as dead.
 *
 * The same "a few, not one" rule as `MIN_SAMPLES_TO_JUDGE`: one failure is a
 * blip, and falling back on a blip flaps the source mid-gate. Three is also
 * cheap to wait for — a fast 5xx costs one round trip each, and even three
 * 1.5 s timeouts are over well before a user gives up on the attempt.
 */
const FAILURES_TO_DEGRADE = 3;

/** Fixed ring: this runs per frame and must not allocate. */
const RTT_WINDOW = 32;

export interface RemotePoseStats {
  serverPoseRequests: number;
  serverPoseFailures: number;
  serverPoseDropped: number;
  serverPoseRttP50Ms: number | null;
  serverPoseRttP90Ms: number | null;
}

export interface RemotePoseClientDeps {
  endpoint: string;
  fetchImpl: typeof fetch;
  encodeFrame: (image: ImageData) => Promise<Blob | null>;
  getIdToken: () => Promise<string | null>;
  now: () => number;
  maxInFlight?: number;
  timeoutMs?: number;
}

export class RemotePoseClient {
  private deps: RemotePoseClientDeps;
  private maxInFlight: number;
  private timeoutMs: number;
  private inFlight = 0;
  private nextRequestId = 0;

  private rtts = new Float64Array(RTT_WINDOW);
  private rttHead = 0;
  private rttCount = 0;

  private requests = 0;
  private failures = 0;
  private dropped = 0;

  /**
   * Server-side failures since the last success. Untouched by a dropped frame
   * and by a local fault (no token, unencodable frame) — neither says anything
   * about the connection.
   */
  private consecutiveFailures = 0;

  constructor(deps: RemotePoseClientDeps) {
    this.deps = deps;
    this.maxInFlight = deps.maxInFlight || DEFAULT_MAX_IN_FLIGHT;
    this.timeoutMs = deps.timeoutMs || DEFAULT_TIMEOUT_MS;
  }

  /**
   * One frame in, `Pose[]` out — the same shape the worker resolves, so
   * `PoseDetectionManager.detectPose`'s callers cannot tell the difference.
   *
   * @param opts.tick rotation tick, echoed by the server. Omitted on alignment.
   */
  async detect(image: ImageData, opts?: { tick?: number }): Promise<Pose[]> {
    // TAKE the slot in the same synchronous step as the check.
    //
    // Checking here and incrementing after the awaits below does not bound
    // anything: three callers in the same tick all await `getIdToken()` before
    // any of them has incremented, so all three see a free window and all three
    // proceed. A bounded window silently becomes unbounded, on exactly the
    // devices it exists for. Caught by
    // "drops a frame once the window is full rather than queueing it".
    if (this.inFlight >= this.maxInFlight) {
      this.dropped++;
      return [];
    }
    this.inFlight++;

    // Everything after the increment is inside the try, so the single `finally`
    // is the only place the slot is released. A leaked slot is worse than a
    // dropped frame: the window closes permanently and the client stops
    // detecting for the rest of the scan, with nothing logged anywhere.
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const token = await this.deps.getIdToken();
      if (!token) {
        // Not a server failure. Do not spend a request or count one against
        // the connection — the fault is local, and the router's own signals
        // already cover it.
        return [];
      }

      const blob = await this.deps.encodeFrame(image);
      if (!blob) return [];

      const requestId = String(++this.nextRequestId);
      const form = new FormData();
      form.append("frame", blob, "frame.jpg");
      form.append("requestId", requestId);
      if (opts && typeof opts.tick === "number") {
        form.append("tick", String(opts.tick));
      }

      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      timer = setTimeout(function () {
        if (controller) controller.abort();
      }, this.timeoutMs);

      this.requests++;
      const startedAt = this.deps.now();

      // NO `cache` OPTION HERE. Not an omission — it is the fix for BUG-0016.
      //
      // `Authorization` is not a CORS-safelisted request header, so this POST
      // is preflighted. The server answers that preflight with
      // `Access-Control-Max-Age: 3600`, which should cover a whole scan. But
      // Chromium marks any request whose cache mode is `no-store`, `no-cache`
      // or `reload` as *revalidating*, and a revalidating request skips the
      // preflight-cache lookup outright — so the Max-Age it just received is
      // never consulted and EVERY frame pays two network round trips.
      //
      // This ran with `cache: "no-store"` until 2026-09-16: Android Chrome had
      // sent 466 preflights for 466 POSTs against dev, a ratio of exactly 1.00,
      // which at 210 ms Cairo→us-central1 was ~37% of the handset's 574 ms per
      // frame. WebKit does not couple the two, which is why iOS never showed
      // it. Full measurement in TRAPS.md.
      //
      // There is nothing to suppress: a POST response is not stored by the HTTP
      // cache to begin with. `EndpointProbe` keeps its `no-store` on purpose —
      // a GET with no custom header is a simple request, so it is never
      // preflighted and the flag is free there.
      const res = await this.deps.fetchImpl(this.deps.endpoint + "/v1/pose", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: form,
        signal: controller ? controller.signal : undefined,
      } as RequestInit);

      if (!res || !res.ok) {
        this.recordFailure();
        return [];
      }

      const data = await res.json();
      if (!data || !data.poses) {
        this.recordFailure();
        return [];
      }

      this.recordRtt(this.deps.now() - startedAt);
      this.consecutiveFailures = 0;

      return rescalePoses(
        data.poses,
        data.frameWidth || SEND_WIDTH,
        data.frameHeight || SEND_HEIGHT,
        image.width,
        image.height
      );
    } catch (e) {
      this.recordFailure();
      return [];
    } finally {
      if (timer !== null) clearTimeout(timer);
      this.inFlight--;
    }
  }

  private recordFailure(): void {
    this.failures++;
    this.consecutiveFailures++;
  }

  private recordRtt(ms: number): void {
    this.rtts[this.rttHead] = ms;
    this.rttHead = (this.rttHead + 1) % RTT_WINDOW;
    if (this.rttCount < RTT_WINDOW) this.rttCount++;
  }

  private percentile(p: number): number | null {
    if (this.rttCount === 0) return null;
    const sample: number[] = [];
    for (let i = 0; i < this.rttCount; i++) sample.push(this.rtts[i]);
    sample.sort(function (a, b) {
      return a - b;
    });
    const idx = Math.min(sample.length - 1, Math.floor(sample.length * p));
    return Math.round(sample[idx]);
  }

  getStats(): RemotePoseStats {
    return {
      serverPoseRequests: this.requests,
      serverPoseFailures: this.failures,
      serverPoseDropped: this.dropped,
      serverPoseRttP50Ms: this.percentile(0.5),
      serverPoseRttP90Ms: this.percentile(0.9),
    };
  }

  /**
   * Called at each ALIGNMENT entry so a retry's numbers are its own — and so a
   * retry gets a fresh try at the server instead of inheriting a verdict about
   * the last attempt's connection.
   */
  resetStats(): void {
    this.rttHead = 0;
    this.rttCount = 0;
    this.requests = 0;
    this.failures = 0;
    this.dropped = 0;
    this.consecutiveFailures = 0;
  }

  /**
   * Whether the connection is too slow — or too dead — to be worth using.
   *
   * Drives the router's live fallback to the device path. Two ways to be
   * degraded:
   *
   *  - the median SUCCESSFUL round trip is over the threshold; or
   *  - the last few requests all FAILED. The median alone is blind to this: a
   *    server that is down, or an upload that times out on every frame, never
   *    produces a round trip to measure, so the fallback could never fire for
   *    exactly the connections that most need it.
   *
   * Both deliberately need a few observations: one slow or failed request is a
   * blip, and falling back on a blip would flap a user between two pose sources
   * mid-gate — which is jitter, the exact thing this whole design exists to
   * avoid.
   */
  isDegraded(thresholdMs: number): boolean {
    if (this.consecutiveFailures >= FAILURES_TO_DEGRADE) return true;
    if (this.rttCount < MIN_SAMPLES_TO_JUDGE) return false;
    const p50 = this.percentile(0.5);
    return p50 !== null && p50 > thresholdMs;
  }
}

/**
 * Map keypoints from the SENT frame back into the ORIGINAL frame's space.
 *
 * The step that is easy to skip and impossible to notice. The server answers in
 * the 288x384 it was handed, but `emitJointCoordinates` scales against the
 * original frame — so leaving them in send-space places every joint at roughly a
 * fifth of its true position. The boxes then never go green, and it looks
 * exactly like a genuine detector failure.
 *
 * Exported for its own tests.
 */
export function rescalePoses(
  poses: Pose[],
  fromWidth: number,
  fromHeight: number,
  toWidth: number,
  toHeight: number
): Pose[] {
  if (!poses || !poses.length) return [];
  if (!fromWidth || !fromHeight) return poses;
  const sx = toWidth / fromWidth;
  const sy = toHeight / fromHeight;
  const out: Pose[] = [];
  for (let i = 0; i < poses.length; i++) {
    const pose = poses[i];
    const kps = pose.keypoints || [];
    const scaled: Pose["keypoints"] = [];
    for (let k = 0; k < kps.length; k++) {
      const kp = kps[k];
      scaled.push({
        x: kp.x * sx,
        y: kp.y * sy,
        score: kp.score,
        name: kp.name,
      });
    }
    out.push({ keypoints: scaled, score: pose.score } as Pose);
  }
  return out;
}
